import { Rocket } from "lucide-react";
import { Shield } from "lucide-react";
import { Package, Layers, ShoppingCart, Receipt } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { useIdleLogout } from "../../hooks/useIdleLogout";
import { Menu, X, LogOut, Moon, Sun, Wifi, Users, DollarSign, LayoutDashboard, UserCog, Globe, Building2, MessageSquare, MapPin, CreditCard, Percent } from "lucide-react";
import CreateHotspotModal from "@/components/CreateHotspotModal";
import i18n from "@/i18n";
import { useTranslation } from "react-i18next";

export default function AdminLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const { user, logout, theme, toggleTheme } = useApp();
  useIdleLogout(logout);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const dragStartX = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [dragPosition, setDragPosition] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [language, setLanguage] = useState<"EN" | "FR">(
    i18n.resolvedLanguage?.startsWith("fr") ? "FR" : "EN"
  );

  const menuItems = [
    {
      label: t("admin.team.menu", { defaultValue: "Team" }),
      path: "/kxt-mgmt/team",
      icon: Shield,
      superAdminOnly: true,
    },
    {
      label: t("admin.layout.menu.launch", { defaultValue: "Launch" }),
      path: "/kxt-mgmt/launch",
      icon: Rocket,
      header: t("admin.layout.header.launch", { defaultValue: "Launch Countdown" }),
      description: t("admin.layout.description.launch", { defaultValue: "Control the homepage launch countdown modal" }),
    },
    { 
      label: t("admin.layout.menu.overview", { defaultValue: "Overview" }), 
      path: "/kxt-mgmt", 
      icon: LayoutDashboard,
      header: t("admin.layout.header.dashboard", { defaultValue: "Dashboard" }),
      description: t("admin.layout.description.dashboard", { defaultValue: "Overview and management of platform activities" })
    },  
    { 
      label: t("admin.layout.menu.users", { defaultValue: "Users" }), 
      path: "/kxt-mgmt/user-management", 
      icon: UserCog,
      header: t("admin.layout.header.users", { defaultValue: "User Management Dashboard" }),
      description: t("admin.layout.description.users", { defaultValue: "Manage and monitor user accounts and activities" })
    },
    { 
      label: t("admin.layout.menu.hotspots", { defaultValue: "Hotspots" }), 
      path: "/kxt-mgmt/hotspots", 
      icon: Wifi,
      header: t("admin.layout.header.hotspots", { defaultValue: "Hotspots Dashboard" }),
      description: t("admin.layout.description.hotspots", { defaultValue: "Monitor and configure network hotspots" })
    },
    {
      label: t("admin.layout.menu.plans", { defaultValue: "Plans" }),
      path: "/kxt-mgmt/plans",
      icon: CreditCard,
      header: t("admin.layout.header.plans", { defaultValue: "Subscription Plans" }),
      description: t("admin.layout.description.plans", { defaultValue: "Create, edit and manage subscription plans" }),
    },
    {
      label: t("admin.layout.menu.currencyMarkets", { defaultValue: "Currency & Markets" }),
      path: "/kxt-mgmt/currency-markets",
      icon: Globe,
      header: t("admin.layout.header.currencyMarkets", { defaultValue: "Currency & Markets" }),
      description: t("admin.layout.description.currencyMarkets", { defaultValue: "Serving currency per country, and which markets are switched on" }),
    },
    {
      label: t("admin.layout.menu.rateCards", { defaultValue: "Rate Cards" }),
      path: "/kxt-mgmt/rate-cards",
      icon: Percent,
      header: t("admin.layout.header.rateCards", { defaultValue: "Rate Cards" }),
      description: t("admin.layout.description.rateCards", { defaultValue: "The versioned revenue split (60/30/10) per country" }),
    },
    {
      label: t("admin.layout.menu.activationFees", { defaultValue: "Activation Fees" }),
      path: "/kxt-mgmt/activation-fees",
      icon: Receipt,
      header: t("admin.layout.header.activationFees", { defaultValue: "Activation Fees" }),
      description: t("admin.layout.description.activationFees", { defaultValue: "Set one-time BYO activation fees per country and hardware class" }),
    },
    {
      label: t("admin.layout.menu.kits", { defaultValue: "Kits" }),
      path: "/kxt-mgmt/kits",
      icon: Package,
    },
    {
      label: t("admin.layout.menu.kitTiers", { defaultValue: "Kit Tiers & Fees" }),
      path: "/kxt-mgmt/kit-tiers",
      icon: Layers,
    },
    {
      label: t("admin.layout.menu.products", { defaultValue: "Products" }),
      path: "/kxt-mgmt/products",
      icon: Package,
    },
    {
      label: t("admin.layout.menu.orders", { defaultValue: "Orders" }),
      path: "/kxt-mgmt/orders",
      icon: ShoppingCart,
    },
    {
      label: t("admin.layout.menu.coverageDemand", { defaultValue: "Coverage Demand" }),
      path: "/kxt-mgmt/coverage-demand",
      icon: MapPin,
      header: t("admin.layout.header.coverageDemand", { defaultValue: "Coverage Demand" }),
      description: t("admin.layout.description.coverageDemand", { defaultValue: "Where users are requesting new hotspots" }),
    },
    { 
      label: t("admin.layout.menu.referrals", { defaultValue: "Referrals" }), 
      path: "/kxt-mgmt/referrals", 
      icon: Users,
      header: t("admin.layout.header.referrals", { defaultValue: "Referrals Dashboard" }),
      description: t("admin.layout.description.referrals", { defaultValue: "Track and manage referral programs and rewards" })
    },
    { 
      label: t("admin.layout.menu.payouts", { defaultValue: "Payouts" }), 
      path: "/kxt-mgmt/payouts", 
      icon: DollarSign,
      header: t("admin.layout.header.payouts", { defaultValue: "Payouts Dashboard" }),
      description: t("admin.layout.description.payouts", { defaultValue: "Process and monitor payment transactions" })
    },
    {
      label: t("admin.layout.menu.fiatPayouts", { defaultValue: "Operator Payouts" }),
      path: "/kxt-mgmt/fiat-payouts",
      icon: CreditCard,
      header: t("admin.layout.header.fiatPayouts", { defaultValue: "Operator Payouts" }),
      description: t("admin.layout.description.fiatPayouts", { defaultValue: "Approve fiat payouts against operator ledger balances" })
    },
    {
      label: t("admin.layout.menu.fundDistribution", { defaultValue: "Fund Distribution" }),
      path: "/kxt-mgmt/fund-distribution",
      icon: DollarSign,
      header: t("admin.layout.header.fundDistribution", { defaultValue: "Fund Distribution" }),
      description: t("admin.layout.description.fundDistribution", { defaultValue: "Review and distribute payments to organisations" }),
    },
    {
      label: t("admin.layout.menu.organisation", { defaultValue: "Organisation" }),
      path: "/kxt-mgmt/organisation",
      icon: Building2,
      header: t("admin.layout.header.organisation", { defaultValue: "Organisation Dashboard" }),
      description: t("admin.layout.description.organisation", { defaultValue: "View organisations and operator requests to become organisations" }),
    },
    {
      label: t("admin.layout.menu.countryManager", { defaultValue: "Country Manager" }),
      path: "/kxt-mgmt/country-manager",
      icon: Globe,
      header: t("admin.layout.header.countryManager", { defaultValue: "Country Manager Dashboard" }),
      description: t("admin.layout.description.countryManager", { defaultValue: "View country managers and operator requests to become country managers" }),
    },
    {
      label: t("admin.layout.menu.support", { defaultValue: "Support" }),
      path: "/kxt-mgmt/support",
      icon: MessageSquare,
      header: t("admin.layout.header.support", { defaultValue: "Support Tickets" }),
      description: t("admin.layout.description.support", { defaultValue: "View and respond to user support tickets" }),
    },
  ];

  const currentMenuItem = menuItems.find(item => item.path === location.pathname) || menuItems[0];

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setLanguage(lng?.startsWith("fr") ? "FR" : "EN");
    };
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    dragStartX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || !isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - dragStartX.current;
    
    if (sidebarOpen) {
      if (diff < 0) {
        setDragPosition(Math.max(diff, -256));
      }
    } else {
      if (dragStartX.current < 20 && diff > 0) {
        setDragPosition(Math.min(diff - 256, 0));
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isDragging) return;
    setIsDragging(false);
    
    if (Math.abs(dragPosition) > 128) {
      setSidebarOpen(!sidebarOpen);
    }
    
    setDragPosition(0);
  };

  const handleHotspotCreated = (hotspot: Record<string, unknown>) => {
    console.log("Hotspot created successfully!", hotspot);
    window.dispatchEvent(new CustomEvent('hotspotCreated', { detail: hotspot }));
  };

  function getInitials(name?: string, email?: string): string {
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      } else if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
    }
    
    if (email) {
      const emailPrefix = email.split('@')[0];
      const words = emailPrefix.match(/[A-Za-z]+/g) || [];
      if (words.length >= 2) {
        return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
      }
      return emailPrefix.substring(0, 2).toUpperCase();
    }
    
    return 'U';
  }

  return (
    <div className={`flex h-screen overflow-hidden ${theme === 'dark' ? 'bg-black' : 'bg-background'}`}>
      <CreateHotspotModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleHotspotCreated}
      />

      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div
        ref={sidebarRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: isMobile && isDragging ? `translateX(${dragPosition}px)` : undefined,
          width: isMobile ? '256px' : sidebarOpen ? '256px' : '80px',
          minWidth: isMobile ? '256px' : sidebarOpen ? '256px' : '80px',
          maxWidth: isMobile ? '256px' : sidebarOpen ? '256px' : '80px',
        }}
        className={`
          ${isMobile ? 'fixed' : 'relative'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-gray-100 border-gray-200'}
          border-r flex flex-col h-full z-50
          ${isMobile ? 'top-0 left-0' : ''}
        `}
      >
        <div className={`p-4 ${sidebarOpen || isMobile ? 'h-[76px]' : 'h-[76px]'}`}>
          <div className="flex items-center justify-between h-full">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center w-full'} focus:outline-none hover:opacity-80`}
            >
              {(sidebarOpen || isMobile) ? (
                <img
                  src={theme === 'dark' ? '/KonnectXLogo-dark.png' : '/KonnectXLogo-light.png'}
                  alt="KonectX Logo"
                  className="h-10 object-contain cursor-pointer"
                />
              ) : (
                <img
                  src={theme === 'dark' ? '/icons/favicon-white.png' : '/icons/favicon-blue.png'}
                  alt="KonectX Icon"
                  className="w-12 h-12 object-contain cursor-pointer"
                />
              )}
            </button>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(false)}
                className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
              >
                <X size={20} className={theme === 'dark' ? 'text-white' : 'text-gray-800'} />
              </button>
            )}
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.filter((item) => !item.superAdminOnly || ["admin","super-admin"].includes(user?.role)).map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setSidebarOpen(false);
                }}
                className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
                  isActive
                    ? 'bg-black text-white dark:bg-blue-600'
                    : theme === 'dark'
                      ? 'text-gray-300 hover:bg-blue-600 hover:text-white'
                      : 'text-gray-700 hover:bg-gray-900 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {(sidebarOpen || isMobile) && <span className="font-medium text-base whitespace-nowrap">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className={`border-t ${theme === 'dark' ? 'border-gray-800' : 'border-gray-300'}`}>
          <div className="p-4 space-y-2">
            <button
              onClick={toggleTheme}
              className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:bg-gray-800' 
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              {theme === "light" ? <Moon className="w-5 h-5 flex-shrink-0" /> : <Sun className="w-5 h-5 flex-shrink-0" />}
              {(sidebarOpen || isMobile) && <span className="font-medium text-base whitespace-nowrap">{theme === "light" ? t("admin.layout.darkMode", { defaultValue: "Dark Mode" }) : t("admin.layout.lightMode", { defaultValue: "Light Mode" })}</span>}
            </button>
          
            <button
              onClick={logout}
              className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg hover:text-red-500 ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:bg-gray-800' 
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {(sidebarOpen || isMobile) && <span className="font-medium text-base whitespace-nowrap">{t("admin.layout.logout", { defaultValue: "Logout" })}</span>}
            </button>
          </div>

          {(sidebarOpen || isMobile) && user && (
             <Link to="" className="block">
            <div className={`p-4 border-t ${theme === 'dark' ? 'border-gray-800' : 'border-gray-300'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                  {user.initials || getInitials(user.name, user.email)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                    {user.displayName || t("admin.layout.user", { defaultValue: "User" })}
                  </p>
                  <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {user.email}
                  </p>
                </div>
              </div>
            </div>
            </Link>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden w-full">
        <header className={`px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between relative ${theme === 'dark' ? 'bg-black border-b border-gray-800' : 'bg-gray-50 border-b border-gray-200'}`}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition ${
                  theme === 'dark' ? 'hover:bg-gray-900 text-white' : 'hover:bg-gray-200 text-gray-900'
                }`}
              >
                <Menu size={24} className="flex-shrink-0" />
              </button>
            )}

            <div className="flex-1 min-w-0">
              <h1 className={`text-xl sm:text-2xl font-bold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {currentMenuItem.header}
              </h1>
              <p className={`text-xs sm:text-sm truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                {currentMenuItem.description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => {
                const next = language === "EN" ? "fr" : "en";
                localStorage.setItem("i18nextLng", next);
                i18n.changeLanguage(next);
              }}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border transition-colors ${
                theme === 'dark'
                  ? 'bg-[#1f2022] border-[#4a4a4c] text-gray-200 hover:bg-[#2a2b2d]'
                  : 'bg-white border-[#d1d5db] text-[#374151] hover:bg-gray-50'
              }`}
              aria-label={`Switch language to ${language === "EN" ? "French" : "English"}`}
            >
              <Globe className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-300' : 'text-[#374151]'}`} />
              <span className="text-xs sm:text-sm font-semibold tracking-wide">{language}</span>
            </button>
          </div>
        </header>

       <main className={`flex-1 overflow-auto p-4 sm:p-6 ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
  <Outlet />
</main>
      </div>
    </div>
  );
}
